import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import {
  trigger,
  transition,
  style,
  animate,
} from '@angular/animations';
import { NgClass } from '@angular/common';
import { OverlayScrollLockService } from '@core/services/overlay-scroll-lock.service';

@Component({
  selector: 'app-modal',
  standalone: true,
  imports: [NgClass],
  animations: [
    // Backdrop fades
    trigger('backdrop', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('180ms ease-out', style({ opacity: 1 })),
      ]),
      transition(':leave', [animate('150ms ease-in', style({ opacity: 0 }))]),
    ]),
    // Panel slides from right -> left
    trigger('panel', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate(
          '220ms cubic-bezier(.22,1,.36,1)',
          style({ transform: 'translateX(0%)' })
        ),
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ transform: 'translateX(100%)' })),
      ]),
    ]),
  ],
  templateUrl: './modal-component.html',
  styleUrls: ['./modal-component.scss'],
})
export class ModalComponent implements OnChanges, AfterViewChecked, OnDestroy {
  private scrollLock = inject(OverlayScrollLockService);
  private hasScrollLock = false;
  private overlayAttached = false;

  @ViewChild('overlay') overlayRef?: ElementRef<HTMLElement>;

  /** Controls modal visibility */
  @Input() isOpen = false;
  @Input() isCloseBtnShow = true;
  @Input() closeOnOutsideClick: boolean = true;


  /** Optional title */
  @Input() title = '';
  @Input() modalSize = 'w-full max-w-md';

  /** Emits when modal closes */
  @Output() close = new EventEmitter<void>();

  ngOnChanges(changes: SimpleChanges): void {
    if ('isOpen' in changes) {
      if (this.isOpen) {
        this.overlayAttached = false;
        this.lockBackgroundScroll();
      } else {
        this.detachOverlay();
        this.unlockBackgroundScroll();
      }
    }
  }

  ngAfterViewChecked(): void {
    this.attachOverlayToBody();
  }

  ngOnDestroy(): void {
    this.detachOverlay();
    this.unlockBackgroundScroll();
  }

  /** Avoid fixed overlays being clipped by transformed ancestors (e.g. mobile sidebar). */
  private attachOverlayToBody(): void {
    const el = this.overlayRef?.nativeElement;
    if (!el || this.overlayAttached || typeof document === 'undefined') {
      return;
    }
    if (el.parentElement !== document.body) {
      document.body.appendChild(el);
    }
    this.overlayAttached = true;
  }

  private detachOverlay(): void {
    const el = this.overlayRef?.nativeElement;
    el?.remove();
    this.overlayAttached = false;
  }

  onClose() {
    this.close.emit();
  }

  onBackdropClick() {
    if (this.closeOnOutsideClick) {
      this.onClose();
    }
  }

  private lockBackgroundScroll(): void {
    if (this.hasScrollLock || typeof document === 'undefined') {
      return;
    }

    this.hasScrollLock = true;
    this.scrollLock.lock();
  }

  private unlockBackgroundScroll(): void {
    if (!this.hasScrollLock || typeof document === 'undefined') {
      return;
    }

    this.hasScrollLock = false;
    this.scrollLock.unlock();
  }
}
